import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Groupper, Header, Icon, Input, Message, Modal } from 'react-frontier';
import { useNavigate } from 'react-router-dom';
import API from '../API';
import Validator from '../Validator';
import { BankAccountForm, BankAccountOwnerType, DepositType } from '../Classes';
import { formatCreditCard, getBankFromCLABE, getBankParticipants } from '../Util';

enum Modals{
	SUCCESS = 1,
}

var AddBankAccount =()=>{
	var navigate = useNavigate();
	var participants = useMemo(()=>{
		var seen = new Set<number>();
		return getBankParticipants().filter(bank=>{
			if(seen.has(bank.participant)) return false;
			seen.add(bank.participant);
			return true;
		});
	}, []);
	var [form, setForm] = useState<BankAccountForm>({
		type: DepositType.SPEI,
		owner_type: BankAccountOwnerType.FISICA,
		account_owner: '',
		postal_code: '',
		clabe: '',
		card_number: '',
		bank_participant: null,
		rfc: '',
		curp: '',
	});
	var [detectedBank, setDetectedBank] = useState<string | null>(null);
	var [saving, setSaving] = useState(false);
	var [formErrors, setFormErrors] = useState<string[]>([]);
	var [shownModal, setShownModal] = useState<Modals | null>(null);
	var shouldRedirectAfterClose = useRef(false);

	var closeSuccessModal = ()=>{
		shouldRedirectAfterClose.current = true;
		setShownModal(null);
	};

	useEffect(()=>{
		if(form.type === DepositType.SPEI && form.clabe && form.clabe.length >= 3){
			setDetectedBank(getBankFromCLABE(form.clabe));
		}else{
			setDetectedBank(null);
		}
	}, [form.clabe, form.type]);

	useEffect(()=>{
		if(shownModal !== null || !shouldRedirectAfterClose.current) return;
		shouldRedirectAfterClose.current = false;
		navigate('/profile/accounts');
	}, [navigate, shownModal]);

	var updateForm =<K extends keyof BankAccountForm>(key: K, value: BankAccountForm[K])=>{
		setForm(prev => ({ ...prev, [key]: value }));
	};

	var validate =()=>{
		var cleanCard = (form.card_number || '').replace(/\D/g, '');
		var cleanClabe = (form.clabe || '').replace(/\D/g, '');
		var rfc = (form.rfc || '').trim().toUpperCase();
		var curp = (form.curp || '').trim().toUpperCase();
		var validationData = {
			owner_type: form.owner_type,
			type: form.type,
			account_owner: (form.account_owner || '').trim(),
			postal_code: form.postal_code || '',
			clabe: cleanClabe,
			card_number: cleanCard,
			bank_participant: form.bank_participant,
			rfc,
			curp,
		};

		var { valid, prompts } = Validator(validationData, {
			owner_type: [{ rule: (v)=>!!v, prompt: 'Selecciona el tipo de propietario.' }],
			type: [{ rule: (v)=>!!v, prompt: 'Selecciona el tipo de cuenta.' }],
			account_owner: [{ rule: 'minLength', params: [3], prompt: 'Ingresa el nombre del titular.' }],
			postal_code: [{
				rule: 'postal_code',
				if: form.owner_type === BankAccountOwnerType.FISICA,
				prompt: 'Ingresa un codigo postal valido de 5 digitos.',
			}],
			clabe: [{
				rule: 'interbank',
				if: form.type === DepositType.SPEI,
				prompt: 'Ingresa una CLABE valida.',
			}],
			card_number: [{
				rule: 'creditcard',
				if: form.type === DepositType.CARD,
				prompt: 'Ingresa un numero de tarjeta valido de 16 digitos.',
			}],
			bank_participant: [{
				rule: (v)=>!!v,
				if: form.type === DepositType.CARD,
				prompt: 'Selecciona el banco de la tarjeta.',
			}],
			rfc: [
				{ rule: 'empty', prompt: 'El RFC es obligatorio.' },
				{ rule: 'rfc', prompt: 'El RFC no es valido.' },
			],
			curp: [
				{ rule: 'empty', prompt: 'La CURP es obligatoria.' },
				{ rule: 'curp', prompt: 'La CURP no es valida.' },
			],
		});

		setFormErrors(prompts);
		return valid;
	};

	var onSubmit =()=>{
		if(saving) return;
		if(!validate()) return;

		setSaving(true);
		API.addBankAccount({
			...form,
			account_owner: form.account_owner.trim(),
			postal_code: form.owner_type === BankAccountOwnerType.FISICA ? form.postal_code?.trim() : '',
			clabe: form.type === DepositType.SPEI ? (form.clabe || '').replace(/\D/g, '') : '',
			card_number: form.type === DepositType.CARD ? (form.card_number || '').replace(/\D/g, '') : '',
			bank_participant: form.type === DepositType.CARD ? form.bank_participant : null,
			rfc: form.rfc?.trim().toUpperCase() || '',
			curp: form.curp?.trim().toUpperCase() || '',
		}).then((res)=>{
			if(res.error){
				setFormErrors([res.message || 'No se pudo guardar la cuenta.']);
				return;
			}
			setShownModal(Modals.SUCCESS);
		}).catch(()=>{
			setFormErrors(['Hubo un error inesperado guardando la cuenta.']);
		}).finally(()=>{
			setSaving(false);
		});
	};

	return <div className="team-check-screen">
		<Groupper
			title="Agregar Cuenta"
			titleSize="normal"
			style={{ margin: 0 }}
			actions={
				<div className="team-groupper-actions">
					<Button as="button" type="button" text="Regresar" basic onClick={()=>navigate('/profile/accounts')} />
					<Button as="button" type="button" text={saving ? 'Guardando...' : 'Guardar'} color="orange" iconName="save" onClick={onSubmit} disabled={saving} />
				</div>
			}
		>
			<div className="team-form-stack">
				<Dropdown
					label="Tipo de propietario"
					value={form.owner_type}
					onValueChange={(value)=>updateForm('owner_type', value as BankAccountOwnerType)}
					items={[
						{ text: 'Persona fisica', value: BankAccountOwnerType.FISICA },
						{ text: 'Persona moral', value: BankAccountOwnerType.MORAL },
					]}
				/>
				<Dropdown
					label="Tipo de cuenta"
					value={form.type}
					onValueChange={(value)=>updateForm('type', value as DepositType)}
					items={[
						{ text: 'Transferencia bancaria (SPEI)', value: DepositType.SPEI },
						{ text: 'Tarjeta bancaria', value: DepositType.CARD },
					]}
				/>
				<Input fluid label={form.type === DepositType.CARD ? 'Nombre en la tarjeta' : 'Nombre del titular'} value={form.account_owner} onChange={(val)=>updateForm('account_owner', val)} placeholder="Nombre completo" />
				{form.owner_type === BankAccountOwnerType.FISICA && (
					<Input fluid label="Codigo postal" value={form.postal_code} onChange={(val)=>updateForm('postal_code', (val || '').replace(/\D/g, '').slice(0, 5))} placeholder="12345" />
				)}
				{form.type === DepositType.SPEI ? (
					<div>
						<Input fluid label="CLABE interbancaria" value={form.clabe} onChange={(val)=>updateForm('clabe', (val || '').replace(/\D/g, '').slice(0, 18))} placeholder="012345678901234567" />
						{!!detectedBank && <div className="team-bank-detected">
							<Icon name="check-circle" style={{ margin: 0 }} />
							<span>Banco detectado: {detectedBank}</span>
						</div>}
					</div>
				) : (
					<>
						<Input fluid label="Numero de tarjeta" value={form.card_number} onChange={(val)=>updateForm('card_number', formatCreditCard(val || ''))} placeholder="1234 5678 9012 3456" />
						<Dropdown
							label="Banco"
							search
							placeholder="Selecciona un banco"
							value={form.bank_participant}
							onValueChange={(value)=>updateForm('bank_participant', value as number)}
							items={participants.map(bank=>({
								text: bank.bank_name,
								value: bank.participant,
							}))}
						/>
					</>
				)}
				<div className="team-form-grid">
					<Input fluid label="RFC" value={form.rfc} onChange={(val)=>updateForm('rfc', (val || '').toUpperCase())} placeholder="ABCD123456ABC" maxLength={13} />
					<Input fluid label="CURP" value={form.curp} onChange={(val)=>updateForm('curp', (val || '').toUpperCase())} placeholder="ABCD123456HABCDE12" maxLength={18} />
				</div>
				{!!formErrors.length && <Message type="error" header="Revisa la información" list={formErrors} />}
			</div>
		</Groupper>
		<Modal open={shownModal === Modals.SUCCESS} onClose={closeSuccessModal} size="tiny">
			<Modal.Header>Cuenta agregada</Modal.Header>
			<Modal.Content style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 }}>
				<Icon name="circle-check" size={32} color='green' />
				<Header size="small" text="Tu cuenta ha sido agregada." />
			</Modal.Content>
			<Modal.Actions>
				<Button type="button" text="Aceptar" color="green" onClick={closeSuccessModal} />
			</Modal.Actions>
		</Modal>
	</div>;
};

export default AddBankAccount;
